import { accountAxios, accountRawAxios } from '@/services/api/accountAxios';
import { getDeviceSignature } from '@/utils/deviceSignature';
import type {
  AccountLoginResult,
  AccountMembership,
  AccountSession,
  AccountSessionRecord,
  AccountTokens,
  ApiEnvelope,
  MfaEnrollment,
  MfaStatus,
} from '@/services/api/accountTypes';

/**
 * Organization-realm auth (POST /auth/login), distinct from the candidate
 * portal's /auth/jms/login. A user with an authenticator enrolled gets a
 * two-step login: /auth/login returns a pending ticket, /auth/login/verify
 * exchanges it plus a 6-digit code for the session.
 */
export const accountAuthApi = {
  login: async (email: string, password: string): Promise<AccountLoginResult> => {
    const response = await accountRawAxios.post<ApiEnvelope<AccountLoginResult>>(
      'auth/login',
      { email, password, deviceSignature: getDeviceSignature() }
    );
    return response.data.data;
  },

  /** Step 2 of a TOTP login. `code` accepts an authenticator or recovery code. */
  verifyLoginMfa: async (pendingToken: string, code: string): Promise<AccountSession> => {
    const response = await accountRawAxios.post<ApiEnvelope<AccountSession>>(
      'auth/login/verify',
      { pendingToken, code, deviceSignature: getDeviceSignature() }
    );
    return response.data.data;
  },

  logout: async (): Promise<void> => {
    await accountAxios.post('auth/logout', {});
  },

  /** Every active org membership of the signed-in user. */
  getOrganizations: async (): Promise<{
    organizations: AccountMembership[];
    currentOrganizationId: string | null;
  }> => {
    const response = await accountAxios.get<
      ApiEnvelope<{ organizations: AccountMembership[]; currentOrganizationId: string | null }>
    >('auth/organizations');
    return {
      organizations: response.data.data?.organizations ?? [],
      currentOrganizationId: response.data.data?.currentOrganizationId ?? null,
    };
  },

  /**
   * Re-issues the session against another organization. The response carries
   * only the rotated tokens and the new role — the caller rebuilds the rest of
   * the context from the membership it switched to.
   */
  switchOrganization: async (
    organizationId: string
  ): Promise<{ tokens: AccountTokens; organizationRole?: { id?: string; name?: string } }> => {
    const response = await accountAxios.post<
      ApiEnvelope<{ tokens: AccountTokens; organizationRole?: { id?: string; name?: string } }>
    >('auth/switch-organization', { organizationId, deviceSignature: getDeviceSignature() });
    return response.data.data;
  },

  /** The API field is `oldPassword`; `newPassword` must satisfy the strong policy. */
  changePassword: async (oldPassword: string, newPassword: string): Promise<void> => {
    await accountAxios.post('auth/change-password', { oldPassword, newPassword });
  },

  requestPasswordReset: async (email: string): Promise<void> => {
    await accountAxios.post('auth/password/request-reset', {
      email,
      resetBaseUrl: window.location.origin,
    });
  },

  // ── Sessions ──

  getSessions: async (): Promise<AccountSessionRecord[]> => {
    const response = await accountAxios.get<ApiEnvelope<AccountSessionRecord[]>>('auth/sessions');
    return response.data.data ?? [];
  },

  revokeSession: async (sessionName: string): Promise<void> => {
    await accountAxios.delete(`auth/sessions/${encodeURIComponent(sessionName)}`);
  },

  // ── Two-factor authentication (TOTP) ──

  getMfaStatus: async (): Promise<MfaStatus> => {
    const response = await accountAxios.get<ApiEnvelope<MfaStatus>>('auth/mfa/status');
    return response.data.data;
  },

  /** Mints a secret + otpauth URL. MFA is not on until confirm() succeeds. */
  enrollMfa: async (): Promise<MfaEnrollment> => {
    const response = await accountAxios.post<ApiEnvelope<MfaEnrollment>>('auth/mfa/enroll');
    return response.data.data;
  },

  /** Enables MFA and returns the recovery codes — shown exactly once. */
  confirmMfa: async (code: string): Promise<string[]> => {
    const response = await accountAxios.post<ApiEnvelope<{ recoveryCodes: string[] }>>(
      'auth/mfa/confirm',
      { code }
    );
    return response.data.data.recoveryCodes;
  },

  disableMfa: async (code: string): Promise<void> => {
    await accountAxios.post('auth/mfa/disable', { code });
  },

  regenerateRecoveryCodes: async (code: string): Promise<string[]> => {
    const response = await accountAxios.post<ApiEnvelope<{ recoveryCodes: string[] }>>(
      'auth/mfa/recovery-codes',
      { code }
    );
    return response.data.data.recoveryCodes;
  },

  // ── Profile ──

  getMyTimezone: async (): Promise<{ timezone: string | null }> => {
    const response = await accountAxios.get<ApiEnvelope<{ timezone: string | null }>>('me/timezone');
    return response.data.data;
  },

  updateMyTimezone: async (timezone: string): Promise<void> => {
    await accountAxios.patch('me/timezone', { timezone });
  },
};
